import {
  Anchor,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Flex,
  Grid,
  SimpleGrid,
  SvgBox,
  SvgBoxProps,
  Text,
} from "@govlaunch/web";
import Box from "@govlaunch/web/src/box/Box";
import React from "react";
import { FacebookIcon, LinkedinIcon, TwitterIcon } from "react-share";
import { IProductFieldsFragment } from "../fragments/__generated__/ProductFieldsFragment.generated";
import getHostname from "../utils/getHostname";
import { ProductDetailsCardIcon } from "./icons";
import { FormattedMessage, useIntl } from "react-intl";

interface IProductDetailsCardProps {
  product: IProductFieldsFragment;
}

export default function ProductDetailsCard({ product }: IProductDetailsCardProps) {
  const { socialMedia } = product;
  const intl = useIntl();

  const ASSISTIVE_TECHNOLOGIES = {
    SCREEN_READER: intl.formatMessage({ defaultMessage: 'Screen Reader', id: 'A/EnQi' }),
    SCREEN_MAGNIFICATION: intl.formatMessage({ defaultMessage: 'Screen Magnification', id: '9aqVDo' }),
    TEXT_READER: intl.formatMessage({ defaultMessage: 'Text Reader', id: 'p8ggQu' }),
    SPEECH_INPUT: intl.formatMessage({ defaultMessage: 'Speech Input', id: 'CeYzvW' }),
    ALTERNATIVE_INPUT_DEVICE: intl.formatMessage({ defaultMessage: 'Alternative Input Device', id: 'EnL02F' }),
  };

  return (
    <Card mt={4}>
      <CardHeader>
        <ProductDetailsCardIcon />
        <CardTitle>
          <FormattedMessage defaultMessage="Product Details" id="ee4CDp" />
        </CardTitle>
      </CardHeader>

      <CardBody>
        <Grid gridAutoColumns="max-content" gridColumnGap={4} gridAutoFlow="column">
          {product.releasedYear && (
            <Box>
              <Text fontWeight="semibold" fontSize="sm">
                <FormattedMessage defaultMessage="Year Released" id="L1NbGa" />
              </Text>

              <Text fontSize="sm">{product.releasedYear}</Text>
            </Box>
          )}

          <Box>
            <Text fontWeight="semibold" fontSize="sm">
              <FormattedMessage defaultMessage="Free Trial?" id="b2Dran" />
            </Text>

            {product.freeTrial ? (
              <Anchor external="outside-govlaunch" href={product.freeTrial.linkHref}>
                <Text fontSize="sm">
                  <FormattedMessage defaultMessage="Yes" id="a5msuh" />
                </Text>
              </Anchor>
            ) : (
              <Text fontSize="sm">
                <FormattedMessage defaultMessage="No" id="oUWADl" />
              </Text>
            )}
          </Box>
        </Grid>

        <Box mt={4}>
          <Text fontWeight="semibold" fontSize="sm">
            <FormattedMessage defaultMessage="Pricing Available?" id="Pk7PsC" />
          </Text>

          {product.pricingPage ? (
            <Anchor external="outside-govlaunch" href={product.pricingPage}>
              <Text fontSize="sm">
                <FormattedMessage defaultMessage="Yes" id="a5msuh" />
              </Text>
            </Anchor>
          ) : (
            <Text fontSize="sm">
              <FormattedMessage defaultMessage="No" id="oUWADl" />
            </Text>
          )}
        </Box>

        {product.assistiveTechnologiesCompatibility.length > 0 && (
          <Box mt={4}>
            <Text fontWeight="semibold" fontSize="sm">
              <FormattedMessage defaultMessage="Accessibility" id="fgCBG8" />
            </Text>
            <Text fontSize="sm">
              {product.assistiveTechnologiesCompatibility
                .map(technology => ASSISTIVE_TECHNOLOGIES[technology])
                .join(" / ")}
            </Text>
          </Box>
        )}

        {product.languagesOffered && (
          <Box mt={4}>
            <Text fontWeight="semibold" fontSize="sm">
              <FormattedMessage defaultMessage="Languages" id="GsBRWL" />
            </Text>

            <Text fontSize="sm">{product.languagesOffered}</Text>
          </Box>
        )}

        {product.certifications.length > 0 && (
          <Box mt={4}>
            <Text fontWeight="semibold" fontSize="sm">
              <FormattedMessage defaultMessage="Regulatory Compliance or Certifications" id="MVKIvU" />
            </Text>

            <Text fontSize="sm">{product.certifications.join(" / ")}</Text>
          </Box>
        )}

        {product.openSource && (
          <Box mt={4}>
            <Text fontWeight="semibold" fontSize="sm">
              <FormattedMessage defaultMessage="Open Source" id="Xd0J7Y" />
            </Text>

            <Anchor href={product.openSource.linkHref} fontSize="sm" external="outside-govlaunch">
              {getHostname(product.openSource.linkHref)}
            </Anchor>
          </Box>
        )}

        {socialMedia && hasSocialMedia(socialMedia) && (
          <Box mt={4}>
            <Box height="1px" bg="gray.100" mt={1} />

            <Flex mt={4}>
              <Text color="blue.600">
                <FormattedMessage defaultMessage="Follow us" id="l1XTI5" />
              </Text>

              <Flex marginLeft="auto">
                <SimpleGrid gridAutoFlow="column" gridAutoColumns="max-content" gridColumnGap={2} alignItems="center">
                  {socialMedia.linkedin && (
                    <Anchor href={socialMedia.linkedin} external="outside-redirect" title={`linkedin-${product.name}`}>
                      <LinkedinIcon size={22} round={true} />
                    </Anchor>
                  )}

                  {socialMedia.twitter && (
                    <Anchor href={socialMedia.twitter} external="outside-redirect" title={`twitter-${product.name}`}>
                      <TwitterIcon size={22} round={true} />
                    </Anchor>
                  )}

                  {socialMedia.facebook && (
                    <Anchor href={socialMedia.facebook} external="outside-redirect" title={`facebook-${product.name}`}>
                      <FacebookIcon size={22} round={true} />
                    </Anchor>
                  )}

                  {socialMedia.instagram && (
                    <Anchor href={socialMedia.instagram} external="outside-redirect" title={`instagram-${product.name}`}>
                      <InstagramIcon height={22} />
                    </Anchor>
                  )}

                  {socialMedia.youtube && (
                    <Anchor href={socialMedia.youtube} external="outside-redirect" title={`youtube-${product.name}`}>
                      <YoutubeIcon height={22} width={29} />
                    </Anchor>
                  )}
                </SimpleGrid>
              </Flex>
            </Flex>
          </Box>
        )}
      </CardBody>
    </Card>
  );
}

function hasSocialMedia(socialMedia: IProductFieldsFragment["socialMedia"]) {
  if (!socialMedia) {
    return false;
  }

  return [
    socialMedia.facebook,
    socialMedia.twitter,
    socialMedia.instagram,
    socialMedia.linkedin,
    socialMedia.youtube,
  ].some(Boolean);
}

const InstagramIcon: React.FC<SvgBoxProps> = props => (
  <SvgBox viewBox="0 0 551.034 551.034" {...props}>
    <linearGradient
      id="prefix__a"
      gradientUnits="userSpaceOnUse"
      x1={275.517}
      y1={4.57}
      x2={275.517}
      y2={549.72}
      gradientTransform="matrix(1 0 0 -1 0 554)"
    >
      <stop offset={0} stopColor="#e09b3d" />
      <stop offset={0.3} stopColor="#c74c4d" />
      <stop offset={0.6} stopColor="#c21975" />
      <stop offset={1} stopColor="#7024c4" />
    </linearGradient>
    <path
      d="M386.878 0H164.156C73.64 0 0 73.64 0 164.156v222.722c0 90.516 73.64 164.156 164.156 164.156h222.722c90.516 0 164.156-73.64 164.156-164.156V164.156C551.033 73.64 477.393 0 386.878 0zM495.6 386.878c0 60.045-48.677 108.722-108.722 108.722H164.156c-60.045 0-108.722-48.677-108.722-108.722V164.156c0-60.046 48.677-108.722 108.722-108.722h222.722c60.045 0 108.722 48.676 108.722 108.722v222.722z"
      fill="url(#prefix__a)"
    />
    <linearGradient
      id="prefix__b"
      gradientUnits="userSpaceOnUse"
      x1={275.517}
      y1={4.57}
      x2={275.517}
      y2={549.72}
      gradientTransform="matrix(1 0 0 -1 0 554)"
    >
      <stop offset={0} stopColor="#e09b3d" />
      <stop offset={0.3} stopColor="#c74c4d" />
      <stop offset={0.6} stopColor="#c21975" />
      <stop offset={1} stopColor="#7024c4" />
    </linearGradient>
    <path
      d="M275.517 133C196.933 133 133 196.933 133 275.516s63.933 142.517 142.517 142.517S418.034 354.1 418.034 275.516 354.101 133 275.517 133zm0 229.6c-48.095 0-87.083-38.988-87.083-87.083s38.989-87.083 87.083-87.083c48.095 0 87.083 38.988 87.083 87.083 0 48.094-38.989 87.083-87.083 87.083z"
      fill="url(#prefix__b)"
    />
    <linearGradient
      id="prefix__c"
      gradientUnits="userSpaceOnUse"
      x1={418.31}
      y1={4.57}
      x2={418.31}
      y2={549.72}
      gradientTransform="matrix(1 0 0 -1 0 554)"
    >
      <stop offset={0} stopColor="#e09b3d" />
      <stop offset={0.3} stopColor="#c74c4d" />
      <stop offset={0.6} stopColor="#c21975" />
      <stop offset={1} stopColor="#7024c4" />
    </linearGradient>
    <circle cx={418.31} cy={134.07} r={34.15} fill="url(#prefix__c)" />
  </SvgBox>
);

const YoutubeIcon: React.FC<SvgBoxProps> = props => (
  <SvgBox viewBox="0 0 461 327" {...props}>
    <path
      d="M365.257.393H95.744C42.866.393 0 43.259 0 96.137v134.728c0 52.878 42.866 95.744 95.744 95.744h269.513c52.878 0 95.744-42.866 95.744-95.744V96.137c0-52.878-42.866-95.744-95.744-95.744zm-64.751 169.663l-126.06 60.123c-3.359 1.602-7.239-.847-7.239-4.568V101.607c0-3.774 3.982-6.22 7.348-4.514l126.06 63.881c3.748 1.899 3.683 7.274-.109 9.082z"
      fill="#F61C0D"
      fillRule="nonzero"
    />
  </SvgBox>
);
